import { makeStyles, Theme } from '@material-ui/core/styles'

/**
 * Styles for AuditRoot component
 */
type AuditRootStylesProps = {
    headerHeight: number
    collapsableSelectorHeight: number
}
export const useAuditRootStyles = makeStyles<Theme, AuditRootStylesProps>(
    (theme) => ({
        container: {
            display: 'flex',
            flexDirection: 'column',
            height: ({ headerHeight, collapsableSelectorHeight }) =>
                `calc(100vh - ${headerHeight + collapsableSelectorHeight}px)`,
            padding: theme.spacing(2),
        },
        toolbar: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: theme.spacing(2),
            flexWrap: 'wrap',
            gap: theme.spacing(2),
        },
        toolbarLeft: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(2),
        },
        statsContainer: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
        },
        statTile: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(0.5),
            padding: theme.spacing(0.5, 1),
            borderRadius: theme.spacing(0.5),
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
        },
        statLabel: {
            fontSize: '0.75rem',
            color: theme.palette.text.secondary,
            fontWeight: 500,
        },
        statValue: {
            fontSize: '0.875rem',
            fontWeight: 700,
        },
        statPast: {
            color: theme.palette.grey[600],
        },
        statCurrent: {
            color: theme.palette.success.main,
        },
        statFuture: {
            color: theme.palette.info.main,
        },
        tableContainer: {
            flex: 1,
            overflow: 'auto',
            marginBottom: theme.spacing(2),
        },
        pagination: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: theme.spacing(2),
            padding: theme.spacing(2),
            borderTop: `1px solid ${theme.palette.divider}`,
        },
        headerCell: {
            fontWeight: 700,
            backgroundColor: theme.palette.grey[100],
            whiteSpace: 'nowrap',
            padding: '12px 16px',
            position: 'sticky',
            top: 0,
            zIndex: 1,
        },
        headerCellContent: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            gap: theme.spacing(0.75),
        },
        mismatchBadge: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 24,
            height: 24,
            padding: '0 8px',
            borderRadius: 12,
            fontSize: '0.75rem',
            fontWeight: 600,
            lineHeight: 1,
            backgroundColor: theme.palette.error.light,
            color: 'White',
        },
        headerCellClickable: {
            cursor: 'pointer',
            userSelect: 'none',
            '&:hover': {
                backgroundColor: theme.palette.grey[200],
            },
            '&:focus': {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: -2,
            },
        },
        dataCell: {
            padding: '12px 16px',
            cursor: 'pointer',
            position: 'relative',
            '&:hover': {
                backgroundColor: theme.palette.action.hover,
            },
        },
        cellContent: {
            position: 'relative',
            width: '100%',
            paddingRight: '40px',
        },
        cellValue: {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
        cellIndicators: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: theme.spacing(0.25),
            position: 'absolute',
            right: '16px',
            top: '50%',
            transform: 'translateY(-50%)',
        },
        matchedCell: {
            borderLeft: `4px solid ${theme.palette.success.dark}`,
            backgroundColor: 'rgba(232, 245, 233, 0.5)',
        },
        notMatchedCell: {
            borderLeft: `4px solid ${theme.palette.error.main}`,
            backgroundColor: 'rgba(255, 235, 235, 0.5)',
        },
        missingRentRollCell: {
            borderLeft: `4px solid ${theme.palette.warning.light}`,
            backgroundColor: 'rgba(255, 253, 231, 0.5)',
        },
        missingDocumentCell: {
            borderLeft: `4px solid ${theme.palette.warning.main}`,
            backgroundColor: 'rgba(255, 248, 225, 0.5)',
        },
        loadingState: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: theme.spacing(8),
        },
        emptyState: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: theme.spacing(8),
            color: theme.palette.text.secondary,
        },
    }),
)

/**
 * Styles for AuditResultsDialog component
 */
export const useAuditResultsDialogStyles = makeStyles((theme: Theme) => ({
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    paper: {
        height: '90vh',
        width: '80vw',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        padding: theme.spacing(2),
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        borderBottom: `1px solid ${theme.palette.divider}`,
        flexShrink: 0,
    },
    headerTop: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    headerTopInline: {
        flexWrap: 'wrap',
        gap: theme.spacing(2),
    },
    headerTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    headerTitleText: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.25),
    },
    statusBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        padding: theme.spacing(0.5, 1),
        borderRadius: theme.spacing(0.5),
        fontSize: '0.875rem',
        fontWeight: 500,
    },
    matchedBadge: {
        backgroundColor: theme.palette.success.light,
        color: theme.palette.success.dark,
    },
    notMatchedBadge: {
        backgroundColor: theme.palette.error.light,
        color: 'White',
    },
    missingBadge: {
        backgroundColor: theme.palette.warning.light,
        color: theme.palette.warning.dark,
    },
    valuesContainer: {
        display: 'flex',
        gap: theme.spacing(2),
        alignItems: 'center',
    },
    valuesInline: {
        flexWrap: 'wrap',
        justifyContent: 'flex-start',
    },
    valueBox: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
    },
    valueLabel: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        fontWeight: 500,
        textTransform: 'uppercase',
    },
    valueRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
    },
    valueText: {
        fontSize: '1rem',
        fontWeight: 600,
    },
    popoverContent: {
        padding: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
        minWidth: 260,
    },
    popoverActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(1),
    },
    reviewNote: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        maxWidth: 480,
    },
    reviewNoteActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(1),
    },
    darkButton: {
        '&&': {
            backgroundColor: '#1a1a1a',
            color: '#ffffff',
        },
        '&&:hover': {
            backgroundColor: '#000000',
        },
        '&&.Mui-disabled': {
            backgroundColor: '#4a4a4a',
            color: 'rgba(255, 255, 255, 0.7)',
        },
    },
    reviewNoteButton: {
        maxWidth: 240,
        textTransform: 'none',
    },
    reviewNoteButtonLabel: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: 220,
        display: 'block',
    },
    content: {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        gap: theme.spacing(2),
    },
    viewerContainer: {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    viewerTitle: {
        fontSize: '0.875rem',
        fontWeight: 600,
        color: theme.palette.text.secondary,
        flexShrink: 0,
    },
    viewerFrame: {
        display: 'block',
        width: '100%',
        height: 'calc(90vh - 320px)',
        minHeight: 360,
        border: 'none',
        backgroundColor: theme.palette.background.default,
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: theme.spacing(2),
        paddingTop: theme.spacing(2),
        borderTop: `1px solid ${theme.palette.divider}`,
    },
}))

/**
 * Styles for AuditAttributeDetail dialog component
 */
export const useAuditAttributeDetailStyles = makeStyles((theme: Theme) => ({
    dialogPaper: {
        height: '90vh',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
    },
    dialogTitle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
    },
    darkButton: {
        '&&': {
            backgroundColor: '#1a1a1a',
            color: '#ffffff',
        },
        '&&:hover': {
            backgroundColor: '#000000',
        },
        '&&.Mui-disabled': {
            backgroundColor: '#4a4a4a',
            color: 'rgba(255, 255, 255, 0.7)',
        },
    },
    dialogContent: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
    },
    metaRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(2),
        gap: theme.spacing(2),
    },
    contentGrid: {
        display: 'flex',
        gap: theme.spacing(2),
        flex: 1,
        minHeight: 0,
    },
    sidebar: {
        width: 260,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        minHeight: 0,
        overflow: 'hidden',
    },
    sidebarHeader: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
    },
    sidebarHeaderControls: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    termList: {
        flex: 1,
        overflowY: 'auto',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        padding: 0,
    },
    termListItem: {
        display: 'flex',
        gap: theme.spacing(1),
        alignItems: 'center',
        padding: theme.spacing(1.5),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    termListItemActive: {
        backgroundColor: theme.palette.action.selected,
    },
    statusDot: {
        width: 10,
        height: 10,
        borderRadius: '50%',
        flexShrink: 0,
    },
    statusDotMatched: {
        backgroundColor: theme.palette.success.main,
    },
    statusDotNotMatched: {
        backgroundColor: theme.palette.error.main,
    },
    statusDotMissing: {
        backgroundColor: theme.palette.warning.main,
    },
    detailPanel: {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    detailToolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
    },
    detailToolbarLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    viewerToggle: {
        marginRight: 0,
    },
    pagination: {
        display: 'flex',
        justifyContent: 'center',
        padding: theme.spacing(1),
    },
    promptBar: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1),
        flexShrink: 0,
    },
    promptSummary: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
    },
    promptText: {
        whiteSpace: 'pre-wrap',
        flex: 1,
    },
    promptActions: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
    },
    examplePreview: {
        maxWidth: 280,
        maxHeight: 140,
        objectFit: 'contain',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 4,
    },
    hiddenFileInput: {
        display: 'none',
    },
    loadingState: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(4),
    },
    emptyState: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(4),
    },
}))
